import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { TranslationService } from '@trimble-gcs/i18n';
import { PluralArgs } from '@trimble-gcs/i18n-types';
import { ModusButtonModule } from '@trimble-gcs/modus';
import { filter, map, switchMap } from 'rxjs';
import { ClassificationScheme } from '../../classification/classification-scheme.model';
import { SetPendingSave } from '../../classification/classification.actions';
import { ClassificationService } from '../../classification/classification.service';
import { DialogComponent } from '../../dialog/dialog.component';
import { CANCEL_BUTTON, DialogData } from '../../dialog/dialog.model';
import { DialogService } from '../../dialog/dialog.service';
import { ClearError } from '../../error-handling/error.actions';
import { ErrorState } from '../../error-handling/error.state';
import { LoadingService } from '../../loading/loading.service';
import { injectLogger } from '../../logging/logger-injection';
import { downloadBlob } from '../../utils/download-blob';
import { ClassificationFormState } from './classification-form-state';
import { ClassificationTableComponent } from './classification-table/classification-table.component';
import { classificationTranslations } from './classification.translations';

@UntilDestroy()
@Component({
  selector: 'sd-config-classification',
  imports: [CommonModule, ClassificationTableComponent, ModusButtonModule, MatProgressBarModule],
  providers: [ClassificationFormState],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './classification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClassificationComponent implements OnDestroy {
  private readonly logger = injectLogger('ClassificationComponent');
  private readonly loadingService = inject(LoadingService);
  private readonly classificationService = inject(ClassificationService);
  private readonly store = inject(Store);
  private readonly dialogService = inject(DialogService);
  private readonly translationService = inject(TranslationService);

  readonly state = inject(ClassificationFormState, { self: true });

  readonly translations = classificationTranslations();

  private readonly loadError = this.store.selectSignal(
    ErrorState.hasError('classificationLoadError'),
  );
  private readonly saveError = this.store.selectSignal(
    ErrorState.hasError('classificationSaveError'),
  );
  private readonly resetError = this.store.selectSignal(
    ErrorState.hasError('classificationResetError'),
  );
  private readonly importError = this.store.selectSignal(
    ErrorState.hasError('classificationImportError'),
  );

  readonly hasError = computed(() => {
    return this.loadError() || this.saveError() || this.resetError() || this.importError();
  });

  readonly errorMessage = computed(() => {
    if (this.loadError()) return this.translations.errors.load.text;
    if (this.saveError()) return this.translations.errors.save.text;
    if (this.resetError()) return this.translations.errors.reset.text;
    if (this.importError()) return this.translations.errors.import.text;
    return '';
  });

  readonly isLoading = toSignal(this.loadingService.isLoading$(this), { initialValue: false });
  readonly disableSave = computed(() => this.isLoading() || !this.state.valid());

  readonly classifications = signal<ClassificationScheme[]>([]);

  private importDialogRef: MatDialogRef<DialogComponent> | null = null;
  private resetDialogRef: MatDialogRef<DialogComponent> | null = null;

  constructor() {
    this.loadClassifications();
    this.createPendingSaveEffect();
  }

  ngOnDestroy(): void {
    this.importDialogRef?.close();
    this.resetDialogRef?.close();
  }

  reset() {
    const dialogData = new DialogData(
      this.translations.resetConfirmation.title.text,
      this.translations.resetConfirmation.message.text,
      { text: this.translations.resetConfirmation.confirm.text, color: 'danger' },
      CANCEL_BUTTON,
    );

    this.resetDialogRef = this.dialogService.showMessage(dialogData);

    this.resetDialogRef
      .afterClosed()
      .pipe(
        filter((confirmed) => confirmed ?? false),
        switchMap(() => {
          return this.loadingService.loadFrom<ClassificationScheme[]>(
            this.classificationService.resetClassificationSchemes(),
            this,
          );
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (data) => this.classifications.set(data),
        error: (err) => this.logger.error('Classifications Reset Error', {}, err),
      });
  }

  export() {
    const classifications = this.classifications();
    const content = JSON.stringify(classifications, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    downloadBlob(blob, 'classifications.json');
  }

  cancel() {
    this.classifications.update((data) => [...data]);
  }

  save() {
    this.loadingService
      .loadFrom(this.classificationService.saveClassificationSchemes(this.state.value()), this)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => this.classifications.set(data),
        error: (err) => this.logger.error('Classifications Save Error', {}, err),
      });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);
    input.value = '';
    if (file) this.importFile(file);
  }

  dismissAlert() {
    this.store.dispatch([
      new ClearError('classificationLoadError'),
      new ClearError('classificationSaveError'),
      new ClearError('classificationResetError'),
      new ClearError('classificationImportError'),
    ]);
  }

  private loadClassifications() {
    this.loadingService
      .loadFrom(this.classificationService.getClassificationSchemes(), this)
      .pipe(untilDestroyed(this))
      .subscribe((data) => this.classifications.set(data));
  }

  private createPendingSaveEffect() {
    effect(() => {
      const pending = this.state.dirty();
      this.store.dispatch(new SetPendingSave(pending));
    });
  }

  private importFile(file: File) {
    this.classificationService
      .readAndParseFile(file)
      .pipe(
        switchMap((classifications) => this.confirmImport(classifications)),
        switchMap((classifications) => {
          return this.loadingService.loadFrom<ClassificationScheme[]>(
            this.classificationService.saveClassificationSchemes(classifications),
            this,
          );
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (data) => this.classifications.set(data),
        error: (err) => this.logger.error('Classifications Import Error', {}, err),
      });
  }

  private confirmImport(classifications: ClassificationScheme[]) {
    const pluralArgs: PluralArgs = { count: classifications.length };
    const pluralMap = this.translations.importConfirmation.messages;
    const message = this.translationService.translatePlural(pluralArgs, pluralMap);

    const dialogData = new DialogData(
      this.translations.importConfirmation.title.text,
      message,
      { text: this.translations.importConfirmation.confirm.text, color: 'primary' },
      CANCEL_BUTTON,
    );

    this.importDialogRef = this.dialogService.showMessage(dialogData);

    return this.importDialogRef.afterClosed().pipe(
      filter((confirmed) => confirmed ?? false),
      map(() => classifications),
    );
  }
}
